import { useState, useMemo } from "react";
import { useThemeContext } from "../../theme/ThemeContext";
import { RADIUS, TYPOGRAPHY } from "../../theme/tokens";
import { SA_AIRLINES, LHG_CARRIERS, Button } from "../shared";
import { overlayCentered } from "../../theme/styles";

// Partner airline codes (non-UA, non-LHG display-level)
const PARTNER_CODES = SA_AIRLINES.map(a => a.code).filter(c => c !== "UA");

function formatHour(h) {
  return String(h).padStart(2, "0");
}

export function ActivityHeatmapModal({ messages, onClose }) {
  const { SA, tokens, GLASS } = useThemeContext();
  const [range, setRange] = useState("7d"); // "today" | "7d"

  const today = new Date().toISOString().slice(0, 10);

  // Filter messages to the selected range and partner-only senders
  const filteredMessages = useMemo(() => {
    return messages.filter(m => {
      if (!m.from_airline || m.from_airline === "UA") return false;
      const code = m.from_airline.toUpperCase();
      // Collapse LHG subsidiaries to "LHG"
      const display = LHG_CARRIERS.includes(code) ? "LHG" : code;
      if (!PARTNER_CODES.includes(display)) return false;
      if (range === "today" && m.date !== today) return false;
      return true;
    });
  }, [messages, range, today]);

  // Build matrix: airline → hour → count
  const { matrix, hours, airlines, maxCount } = useMemo(() => {
    const mat = {};
    const hourSet = new Set();
    const airlineSet = new Set();

    filteredMessages.forEach(m => {
      const code = m.from_airline.toUpperCase();
      const airline = LHG_CARRIERS.includes(code) ? "LHG" : code;
      const hour = m.created_at ? new Date(m.created_at).getHours() : null;
      if (hour === null) return;
      airlineSet.add(airline);
      hourSet.add(hour);
      if (!mat[airline]) mat[airline] = {};
      mat[airline][hour] = (mat[airline][hour] || 0) + 1;
    });

    // Show hours 6–23 (operational window)
    const hours = Array.from({ length: 18 }, (_, i) => i + 6);
    const airlines = [...airlineSet].sort();

    // 95th percentile cap
    const counts = Object.values(mat).flatMap(row => Object.values(row));
    counts.sort((a, b) => a - b);
    const p95 = counts[Math.floor(counts.length * 0.95)] || 1;

    return { matrix: mat, hours, airlines, maxCount: Math.max(p95, 1) };
  }, [filteredMessages]);

  const cellSize = 28;
  const labelWidth = 36;
  const hasData = airlines.length > 0;

  return (
    <div style={{ ...overlayCentered, zIndex: 500 }} onClick={onClose}>
      <div style={{ ...GLASS.modal, borderRadius: RADIUS.card, padding: "22px 24px",
        maxWidth: "min(92vw, 640px)", width: "100%", maxHeight: "88vh", overflow: "auto" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: TYPOGRAPHY.sub, color: SA.white, fontWeight: 600,
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
              Partner Activity Heatmap
            </div>
            <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, marginTop: 2,
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
              Messages sent by partners, by hour of day
            </div>
          </div>
          <Button variant="icon" onClick={onClose}>×</Button>
        </div>

        {/* Range toggle */}
        <div style={{ display: "flex", gap: 6, marginBottom: 20 }}>
          {[["today", "Today"], ["7d", "Last 7 Days"]].map(([val, label]) => (
            <Button key={val} variant="pill" active={range === val} size="sm"
              onClick={() => setRange(val)}>
              {label}
            </Button>
          ))}
        </div>

        {!hasData ? (
          <div style={{ padding: "40px 0", textAlign: "center",
            fontSize: TYPOGRAPHY.caption, color: SA.silver,
            fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
            No partner messages in this period
          </div>
        ) : (
          <>
            {/* Hour labels */}
            <div style={{ display: "flex", marginLeft: labelWidth, marginBottom: 4, gap: 2 }}>
              {hours.map(h => (
                <div key={h} style={{ width: cellSize, textAlign: "center",
                  fontSize: TYPOGRAPHY.tiny, color: SA.silver,
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                  {formatHour(h)}
                </div>
              ))}
            </div>

            {/* Rows */}
            {airlines.map(airline => (
              <div key={airline} style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 3 }}>
                {/* Airline label */}
                <div style={{ width: labelWidth, fontSize: TYPOGRAPHY.caption, color: SA.lightSilver,
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", flexShrink: 0, textAlign: "right",
                  paddingRight: 8 }}>
                  {airline}
                </div>
                {/* Cells */}
                {hours.map(h => {
                  const count = (matrix[airline] && matrix[airline][h]) || 0;
                  const opacity = count === 0 ? 0 : 0.12 + 0.78 * Math.min(count / maxCount, 1);
                  const bg = count === 0
                    ? SA.darkGray
                    : `rgba(136,170,221,${opacity})`; // tokens.partner base color
                  return (
                    <div key={h}
                      title={count > 0 ? `${airline} @ ${formatHour(h)}:00 — ${count} message${count !== 1 ? "s" : ""}` : undefined}
                      style={{ width: cellSize, height: cellSize, borderRadius: RADIUS.sharp,
                        background: bg, border: `1px solid ${SA.borderGray}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: TYPOGRAPHY.tiny, color: count > 0 ? SA.white : "transparent",
                        fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                        transition: "background 0.15s" }}>
                      {count > 0 && count}
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Legend */}
            <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8,
              fontSize: TYPOGRAPHY.tiny, color: SA.silver,
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
              <span>Less</span>
              {[0.12, 0.35, 0.58, 0.78, 0.95].map((op, i) => (
                <div key={i} style={{ width: 14, height: 14, borderRadius: RADIUS.sharp,
                  background: `rgba(136,170,221,${op})`, border: `1px solid ${SA.borderGray}` }} />
              ))}
              <span>More</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
